import type React from 'react';
import type { CalculationMode, CalculationResult, AmortizationSummary } from './types';
import { formatCurrency, getModeTitle } from './calculations';

export interface PaymentSummaryProps {
  calculation: AmortizationSummary;
  calcResult: CalculationResult;
  calcMode: CalculationMode;
}

export const PaymentSummary: React.FC<PaymentSummaryProps> = ({
  calculation,
  calcResult,
  calcMode,
}) => {
  const principalPercent =
    calculation.totalAmount > 0 ? (calculation.totalPrincipal / calculation.totalAmount) * 100 : 0;
  const interestPercent =
    calculation.totalAmount > 0 ? (calculation.totalInterest / calculation.totalAmount) * 100 : 0;
  const strokeDashPrincipal = (principalPercent / 100) * 282.74;
  const strokeDashInterest = (interestPercent / 100) * 282.74;

  return (
    <div className="lg:col-span-5 flex flex-col justify-between bg-white rounded-3xl p-6 border border-gray-200 shadow-sm">
      <div>
        <div className="flex items-center justify-between border-b border-gray-100 pb-3">
          <h2 className="text-lg font-extrabold text-gray-900">Payment Summary</h2>
          <span className="text-[11px] font-bold text-brand-800 bg-brand-50 border border-brand-200 px-2.5 py-0.5 rounded-full">
            Output: {getModeTitle(calcMode)}
          </span>
        </div>

        {/* Main EMI Highlight Card */}
        <div className="mt-4 bg-brand-50/70 rounded-2xl p-5 border border-brand-200/80 shadow-2xs">
          <span className="text-xs font-bold uppercase tracking-wider text-brand-800">
            Monthly EMI Payable
          </span>
          <div className="mt-1 text-3xl sm:text-4xl font-extrabold tracking-tight text-gray-900">
            {formatCurrency(calculation.emi)}
          </div>
          <p className="mt-1 text-xs text-gray-600 font-medium">
            Fixed monthly installment for {calcResult.resolvedTotalMonths} months
            {calcResult.resolvedTotalMonths > 0 && (
              <span>
                {' '}
                ({calcResult.resolvedYears} Yrs{' '}
                {calcResult.resolvedMonths > 0 ? `${calcResult.resolvedMonths} Mos` : ''})
              </span>
            )}
          </p>
          {calculation.hasFinalAdjustment && (
            <p className="mt-1 text-[11px] text-brand-700 font-semibold">
              * Final installment of {formatCurrency(calculation.finalMonthEmi)} adjusted for
              currency rounding
            </p>
          )}
        </div>

        {/* Donut Visual & Key Metrics */}
        <div className="mt-6 flex flex-col sm:flex-row items-center gap-6 bg-gray-50/80 p-4 rounded-2xl border border-gray-150">
          {/* Ring Donut SVG */}
          <div className="relative flex items-center justify-center shrink-0 w-28 h-28">
            <svg className="w-28 h-28 transform -rotate-90" viewBox="0 0 100 100">
              {/* Background Circle */}
              <circle
                cx="50"
                cy="50"
                r="45"
                className="text-gray-200"
                strokeWidth="10"
                stroke="currentColor"
                fill="transparent"
              />
              {/* Principal Arc (Brand Green) */}
              <circle
                cx="50"
                cy="50"
                r="45"
                className="text-brand-600 transition-all duration-500 ease-out"
                strokeWidth="10"
                strokeDasharray={`${strokeDashPrincipal} 282.74`}
                strokeDashoffset="0"
                strokeLinecap="round"
                stroke="currentColor"
                fill="transparent"
              />
              {/* Interest Arc (Amber) */}
              <circle
                cx="50"
                cy="50"
                r="45"
                className="text-amber-500 transition-all duration-500 ease-out"
                strokeWidth="10"
                strokeDasharray={`${strokeDashInterest} 282.74`}
                strokeDashoffset={`-${strokeDashPrincipal}`}
                strokeLinecap="round"
                stroke="currentColor"
                fill="transparent"
              />
            </svg>
            <div className="absolute flex flex-col items-center justify-center text-center select-none">
              <span className="text-[10px] uppercase font-bold text-gray-400">Interest</span>
              <span className="text-xs font-extrabold text-amber-600">
                {calculation.interestRatio.toFixed(1)}%
              </span>
            </div>
          </div>

          {/* Legend Metrics */}
          <div className="flex-1 space-y-2.5 w-full">
            <div className="flex items-center justify-between text-xs">
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-brand-600 shrink-0" />
                <span className="font-semibold text-gray-600">Principal Amount:</span>
              </div>
              <span className="font-extrabold text-gray-900">
                {formatCurrency(calculation.totalPrincipal)}
              </span>
            </div>

            <div className="flex items-center justify-between text-xs">
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-amber-500 shrink-0" />
                <span className="font-semibold text-gray-600">Total Interest:</span>
              </div>
              <span className="font-extrabold text-amber-700">
                {formatCurrency(calculation.totalInterest)}
              </span>
            </div>

            <div className="pt-2 border-t border-gray-200 flex items-center justify-between text-xs font-bold">
              <span className="text-gray-800">Total Amount Payable:</span>
              <span className="text-brand-900 font-extrabold text-sm">
                {formatCurrency(calculation.totalAmount)}
              </span>
            </div>
          </div>
        </div>

        {/* Resolved Parameters Overview Pill */}
        <div className="mt-4 p-3 rounded-xl bg-gray-50 border border-gray-200 text-xs text-gray-600 space-y-1">
          <div className="flex justify-between items-center">
            <span className="font-medium text-gray-500">Effective Rate:</span>
            <span className="font-extrabold text-gray-800">
              {calcResult.isCalculated && !calcResult.error
                ? `${calcResult.resolvedRate.toFixed(2)}% p.a.`
                : '—'}
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span className="font-medium text-gray-500">Total Duration:</span>
            <span className="font-extrabold text-gray-800">
              {calcResult.isCalculated && !calcResult.error && calcResult.resolvedTotalMonths > 0
                ? `${calcResult.resolvedTotalMonths} Months (${calcResult.resolvedYears} Yrs ${
                    calcResult.resolvedMonths > 0 ? `${calcResult.resolvedMonths} Mos` : ''
                  })`
                : '—'}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};
